import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { useFrame } from "@react-three/fiber";

const RenderObject = forwardRef(function RenderObject(
  { model, autoRotate = false, children },
  ref,
) {
  /*
   * Runtime transform.
   *
   * User interaction and auto rotation
   * modify this group.
   */
  const runtimeGroupRef = useRef();

  /*
   * Loaded render meshes.
   */
  const [meshes, setMeshes] = useState([]);

  useEffect(() => {
    let cancelled = false;

    let loadedMeshes = [];

    async function loadModel() {
      try {
        if (model.type === "gltf") {
          loadedMeshes = await model.loadMeshes();
        } else {
          loadedMeshes = [
            {
              name: model.name,

              geometry: model.createGeometry(),
            },
          ];
        }

        if (cancelled) {
          loadedMeshes.forEach((mesh) => mesh.geometry.dispose());

          return;
        }

        setMeshes(loadedMeshes);
      } catch (error) {
        console.error(`Failed to load model "${model.name}"`, error);

        if (!cancelled) {
          setMeshes([]);
        }
      }
    }

    loadModel();

    return () => {
      cancelled = true;

      loadedMeshes.forEach((mesh) => mesh.geometry.dispose());
    };
  }, [model]);

  /*
   * Expose runtime transform controls
   * to RenderingDemo.
   */
  useImperativeHandle(
    ref,
    () => ({
      rotate(deltaX, deltaY) {
        if (!runtimeGroupRef.current) {
          return;
        }

        runtimeGroupRef.current.rotation.y += deltaX;

        runtimeGroupRef.current.rotation.x += deltaY;
      },

      move(deltaX, deltaY, deltaZ) {
        if (!runtimeGroupRef.current) {
          return;
        }

        runtimeGroupRef.current.position.x += deltaX;

        runtimeGroupRef.current.position.y += deltaY;

        runtimeGroupRef.current.position.z += deltaZ;
      },

      reset() {
        if (!runtimeGroupRef.current) {
          return;
        }

        runtimeGroupRef.current.position.set(0, 0, 0);

        runtimeGroupRef.current.rotation.set(0, 0, 0);
      },
    }),
    [],
  );

  /*
   * Auto rotation belongs to the
   * runtime transform, not the model's
   * presentation transform.
   */
  useFrame((_, delta) => {
    if (autoRotate && runtimeGroupRef.current) {
      runtimeGroupRef.current.rotation.y += delta * 0.25;
    }
  });

  /*
   * Model presentation transform.
   *
   * This remains untouched by user
   * interaction.
   */
  const transform = model.transform ?? {};

  const position = transform.position ?? [0, 0, 0];

  const rotation = transform.rotation ?? [0, 0, 0];

  const scale = transform.scale ?? 1;

  if (meshes.length === 0) {
    return null;
  }

  return (
    <group ref={runtimeGroupRef}>
      <group position={position} rotation={rotation} scale={scale}>
        {meshes.map((mesh, index) => (
          <mesh key={`${mesh.name}-${index}`} geometry={mesh.geometry}>
            {children}
          </mesh>
        ))}
      </group>
    </group>
  );
});

export default RenderObject;
